import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  EditOutlined,
} from '@ant-design/icons'
import type { ColumnDef } from '@tanstack/react-table'
import { Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import type { ReactNode } from 'react'

import type { UserInterface } from '@/types'

import type { EditableUserField, UsersSortField } from './types/users.types'
import UserEditField, {
  type UserEditFormContext,
} from './UserEditField/UserEditField'
import { getRoleLabel } from './Users.config'
import cn from './Users.module.scss'

const CREATED_AT_FORMAT = 'DD.MM.YYYY'

const ACCESS_FIELDS: EditableUserField[] = ['enabled', 'roles']

export const USERS_COLUMN_WIDTHS: Partial<Record<string, string>> = {
  enabled: '110px',
  description: 'minmax(180px, 1.2fr)',
  login: 'minmax(150px, 0.9fr)',
  phoneNumber: 'minmax(140px, 0.9fr)',
  email: 'minmax(180px, 1fr)',
  telegram: '150px',
  roles: 'minmax(260px, 1.6fr)',
  createdAt: '140px',
  actions: '100px',
}

export type RenderSortTitle = (
  field: UsersSortField,
  title: string
) => ReactNode

type UsersColumnsContext = {
  editingKey: string
  canManageUsers: boolean
  isSaveDisabled: boolean
  editForm: UserEditFormContext
  canEditUser: (user: UserInterface) => boolean
  renderSortTitle: RenderSortTitle
  onEdit: (user: UserInterface) => void
  onSave: (id: string) => void
  onCancel: () => void
}

const renderStatusIcon = (isActive: boolean) =>
  isActive ? (
    <CheckCircleOutlined className={cn.statusActive} />
  ) : (
    <CloseCircleOutlined className={cn.statusInactive} />
  )

const renderRoles = (user: UserInterface, context: UsersColumnsContext) => {
  const roleLabels = (user.roles ?? []).map((role) =>
    getRoleLabel(role, context.editForm.roleOptions)
  )
  if (!roleLabels.length) return '—'

  const visibleRoles = roleLabels.slice(0, 2)
  const hiddenCount = roleLabels.length - visibleRoles.length

  return (
    <Tooltip title={roleLabels.join(', ')}>
      <div className={cn.rolesDisplay}>
        {visibleRoles.map((role) => (
          <span key={role} className={cn.roleBadge}>
            {role}
          </span>
        ))}
        {hiddenCount > 0 && (
          <span className={cn.roleBadgeMuted}>+{hiddenCount}</span>
        )}
      </div>
    </Tooltip>
  )
}

const renderActions = (user: UserInterface, context: UsersColumnsContext) => {
  if (!context.canEditUser(user)) return null

  if (user.id !== context.editingKey)
    return (
      <Typography.Link
        disabled={context.editingKey !== ''}
        onClick={() => context.onEdit(user)}
        aria-label="Редактировать"
      >
        <EditOutlined className={cn.actionIcon} />
      </Typography.Link>
    )

  return (
    <span className={cn.editActions}>
      <Typography.Link
        disabled={context.isSaveDisabled}
        onClick={() => {
          if (!context.isSaveDisabled) context.onSave(user.id)
        }}
        aria-label="Сохранить"
      >
        <CheckOutlined className={cn.saveIcon} />
      </Typography.Link>
      <Typography.Link onClick={context.onCancel} aria-label="Отменить">
        <CloseCircleOutlined className={cn.cancelIcon} />
      </Typography.Link>
    </span>
  )
}

export const getUsersColumns = (
  context: UsersColumnsContext
): ColumnDef<UserInterface, unknown>[] => {
  const isFieldEditing = (user: UserInterface, field: EditableUserField) =>
    user.id === context.editingKey &&
    (context.canManageUsers || !ACCESS_FIELDS.includes(field)) &&
    (field !== 'roles' || context.editForm.roleOptions.length > 0)

  const withEditField =
    (field: EditableUserField, render: (user: UserInterface) => ReactNode) =>
    ({ row }: { row: { original: UserInterface } }) =>
      isFieldEditing(row.original, field) ? (
        <UserEditField field={field} {...context.editForm} />
      ) : (
        render(row.original)
      )

  return [
    {
      id: 'enabled',
      header: () => context.renderSortTitle('enabled', 'Доступ'),
      cell: withEditField('enabled', (user) => (
        <Tooltip title={user.enabled ? 'Активен' : 'Не активен'}>
          {renderStatusIcon(user.enabled)}
        </Tooltip>
      )),
    },
    {
      id: 'description',
      header: () => context.renderSortTitle('description', 'ФИО'),
      cell: withEditField('description', (user) => user.description || '—'),
    },
    {
      id: 'login',
      header: () => context.renderSortTitle('login', 'Логин'),
      cell: withEditField('login', (user) => user.login || '—'),
    },
    {
      id: 'phoneNumber',
      header: 'Телефон',
      cell: withEditField('phoneNumber', (user) => user.phoneNumber || '—'),
    },
    {
      id: 'email',
      header: 'Email',
      cell: withEditField('email', (user) => user.email || '—'),
    },
    {
      id: 'telegram',
      header: () => context.renderSortTitle('telegramActivated', 'Telegram'),
      cell: ({ row }) => {
        const user = row.original
        if (!user.telegramId) return '—'
        return (
          <Tooltip
            title={
              user.telegramActivated ? 'Telegram подключён' : 'Не подключён'
            }
          >
            <span className={cn.telegram}>
              {renderStatusIcon(user.telegramActivated)}
              {user.telegramId}
            </span>
          </Tooltip>
        )
      },
    },
    {
      id: 'roles',
      header: 'Роли',
      cell: withEditField('roles', (user) => renderRoles(user, context)),
    },
    {
      id: 'createdAt',
      header: () => context.renderSortTitle('createdAt', 'Дата создания'),
      cell: ({ row }) =>
        row.original.createdAt
          ? dayjs(row.original.createdAt).format(CREATED_AT_FORMAT)
          : '—',
    },
    {
      id: 'actions',
      header: 'Действия',
      cell: ({ row }) => renderActions(row.original, context),
    },
  ]
}
